import React, { useEffect, useRef } from "react";

const toc = [
  ["Overview & Types", "overview"],
  ["2D Scaling", "scaling-2d"],
  ["Scale Around a Point", "scale-around-point"],
  ["2D Shear", "shear-2d"],
  ["2D Rotation", "rotation-2d"],
  ["2D Translation", "translation-2d"],
  ["Matrix Fundamentals", "matrices"],
  ["Homogeneous Coordinates", "homogeneous"],
  ["Homogeneous Matrices (2D)", "homogeneous-2d"],
  ["3D Homogeneous Matrices", "homogeneous-3d"],
  ["Basis Vectors", "basis-vectors"],
  ["3D Rotation Matrices", "rotation-3d"],
  ["Extrinsic vs Intrinsic Rotation", "extrinsic-intrinsic"],
  ["Euler Angles & Gimbal Lock", "euler-gimbal"],
  ["Axis-Angle Rotation", "axis-angle"],
  ["Quaternions", "quaternions"],
  ["Compound Transformations", "compound"],
  ["Similarity vs Affine", "similarity-affine"],
  ["Shear from Chained Transforms", "shear-chain"],
  ["Inverse Transformations", "inverse"],
  ["Non-Invertible Transforms", "non-invertible"],
  ["Numerical Accuracy", "numerical"],
];

const F = ({ children }) => (
  <div className="formula-block">
    {(Array.isArray(children) ? children : [children]).map((line, i) => (
      <div key={i} className="formula-row">{line}</div>
    ))}
  </div>
);

const NoteBox = ({ label = "Note", children }) => (
  <div className="note-box">
    <div className="note-label">{label}</div>
    {children}
  </div>
);

const Transformations = ({ renderToc }) => {
  const ref = useRef(null);

  useEffect(() => {
    const tryRender = () => {
      if (window.renderMathInElement && ref.current) {
        window.renderMathInElement(ref.current, {
          delimiters: [
            { left: "$$", right: "$$", display: true },
            { left: "$", right: "$", display: false },
          ],
          throwOnError: false,
        });
      } else {
        setTimeout(tryRender, 100);
      }
    };
    tryRender();
  }, []);

  useEffect(() => {
    if (renderToc) renderToc(toc);
  }, [renderToc]);

  return (
    <div ref={ref}>
      <h1>Transformations</h1>
      <p className="subtitle">
        Translation, Rotation, Scale, and Shear in 2D and 3D &nbsp;·&nbsp; by Jeff Wilson, Ph.D.
      </p>

      {/* ── OVERVIEW ── */}
      <h2 id="overview">Overview &amp; Transformation Types</h2>
      <p>
        A <strong>transformation</strong> maps points or vectors to a new
        configuration — affecting position, orientation, or scale. Most
        transformations are represented as matrices.
      </p>

      <h3>Linear Transformations</h3>
      <p>
        Preserve additivity and homogeneity, and keep the origin fixed.
        Includes: identity, rotation, scale, shear, reflection.{" "}
        <strong>Translation is not linear.</strong>
      </p>

      <h3>Conformal Linear Transformations</h3>
      <p>
        Linear transformations that additionally preserve angles and shape:
        identity, rotation, uniform scale, reflection. Non-uniform scale and
        shear are not conformal.
      </p>

      <h3>Similarity Transformation</h3>
      <p>
        Preserves shape, proportions, angles, and relative distances.
        Allows changes in position, orientation, and <em>uniform</em> scale.
        Sometimes called a rigid transform, though formally a rigid transform
        excludes scaling.
      </p>

      <h3>Affine Transformation</h3>
      <p>
        Preserves collinearity and parallelism. A combination of one or more
        linear transformations followed by a translation. Includes non-uniform
        scale and shear — the origin need not be preserved.
      </p>

      {/* ── SCALING ── */}
      <h2 id="scaling-2d">2D Scaling</h2>
      <p>
        Scaling changes the size of an object by multiplying each coordinate
        by a scaling factor.
      </p>
      <F>
        {[
          "General: $x' = s_x \\cdot x, \\quad y' = s_y \\cdot y$",
          "Uniform ($s_x = s_y = s$): $x' = s\\cdot x, \\quad y' = s\\cdot y$",
        ]}
      </F>
      <ul>
        <li>{"$s = 1$"}: no change &emsp; {"$s > 1$"}: expand &emsp; {"$s < 1$"}: contract</li>
        <li>Uniform scaling preserves shape; non-uniform scaling distorts proportions</li>
      </ul>
      <p>As a matrix:</p>
      <F>
        {[
          "$$S = \\begin{pmatrix} s_x & 0 \\\\ 0 & s_y \\end{pmatrix}, \\quad \\mathbf{p}' = S\\cdot\\mathbf{p}$$",
        ]}
      </F>
      <NoteBox label="Concerns">
        Scaling by zero annihilates a dimension (information loss, no inverse).
        Very large or small factors introduce floating-point rounding errors.
      </NoteBox>

      {/* ── SCALE AROUND POINT ── */}
      <h2 id="scale-around-point">Scale Around a Point</h2>
      <p>
        By default, scaling is relative to the origin. To scale around an
        arbitrary anchor point {"$P$"}:
      </p>
      <ol>
        <li>Translate {"$P$"} to the origin: {"$T(-P)$"}</li>
        <li>Apply the scale: {"$S(s_x, s_y)$"}</li>
        <li>Translate back: {"$T(P)$"}</li>
      </ol>
      <F>{"$$M = T(P)\\cdot S(s_x,s_y)\\cdot T(-P)$$"}</F>
      <p>The same translate-operate-untranslate pattern applies to rotation around an anchor.</p>

      {/* ── SHEAR ── */}
      <h2 id="shear-2d">2D Shear</h2>
      <p>
        A shear slides points in one direction proportional to their position
        in another direction. Shear is linear and preserves area.
      </p>
      <F>
        {[
          "Horizontal shear: $x' = x + sh_x\\cdot y, \\quad y' = y \\qquad sh_x = \\tan\\theta$",
          "Vertical shear:   $x' = x, \\quad y' = y + sh_y\\cdot x$",
        ]}
      </F>
      <p>
        Horizontal shear tilts the {"$x$"}-axis by angle {"$\\theta$"} — the
        {"$x$"} and {"$y$"} axes are no longer orthogonal afterwards.
      </p>
      <p>Matrix forms:</p>
      <F>
        {[
          "$$\\text{shear\\_x}(\\theta) = \\begin{pmatrix} 1 & \\tan\\theta \\\\ 0 & 1 \\end{pmatrix}$$",
          "$$\\text{shear\\_y}(\\theta) = \\begin{pmatrix} 1 & 0 \\\\ \\tan\\theta & 1 \\end{pmatrix}$$",
        ]}
      </F>

      {/* ── ROTATION ── */}
      <h2 id="rotation-2d">2D Rotation</h2>
      <p>
        Rotates a point around the origin by angle {"$\\theta$"} (in radians).
        Distance from the origin is preserved.
      </p>
      <F>
        {[
          "$p_x' = p_x\\cos\\theta - p_y\\sin\\theta$",
          "$p_y' = p_x\\sin\\theta + p_y\\cos\\theta$",
          "$$R(\\theta) = \\begin{pmatrix} \\cos\\theta & -\\sin\\theta \\\\ \\sin\\theta & \\cos\\theta \\end{pmatrix}$$",
        ]}
      </F>
      <ul>
        <li>Positive {"$\\theta$"}: counterclockwise &emsp; Negative {"$\\theta$"}: clockwise</li>
        <li>Rotations preserve angles between vectors</li>
      </ul>

      <h3>Degrees ↔ Radians</h3>
      <F>
        {[
          "$$\\theta_{\\text{rad}} = \\theta_{\\deg} \\cdot \\frac{\\pi}{180}$$",
        ]}
      </F>

      <h3>Angle of a 2D Vector with the +X Axis</h3>
      <F>
        {[
          "$\\theta = \\arctan(y/x)$ — use $\\text{atan2}(y, x)$ in code",
          "$\\text{atan2}$ tracks the signs of both $x$ and $y$, handling all four quadrants and avoiding divide-by-zero.",
        ]}
      </F>

      {/* ── TRANSLATION ── */}
      <h2 id="translation-2d">2D Translation</h2>
      <p>
        Translation moves a point by adding a displacement vector {"$\\mathbf{t}$"}.
        It changes position without altering size, shape, or orientation.
      </p>
      <F>
        {[
          "$$\\mathbf{p}' = \\mathbf{p} + \\mathbf{t} = \\begin{pmatrix}x + t_x \\\\ y + t_y\\end{pmatrix}$$",
        ]}
      </F>
      <NoteBox>
        Translation is <strong>not</strong> a linear transformation — it does
        not preserve the origin. It is handled via homogeneous coordinates.
      </NoteBox>

      {/* ── MATRICES ── */}
      <h2 id="matrices">Matrix Fundamentals</h2>
      <p>
        A matrix {"$M$"} with {"$a$"} rows and {"$b$"} columns can be
        multiplied by a matrix {"$N$"} with {"$b$"} rows and {"$c$"} columns,
        yielding a result {"$R$"} with {"$a$"} rows and {"$c$"} columns:
      </p>
      <F>
        {[
          "$$r_{ij} = \\sum_{k} m_{ik} \\cdot n_{kj}$$",
        ]}
      </F>
      <ul>
        <li>Matrix multiplication is <strong>not commutative</strong>: {"$AB \\ne BA$"} in general</li>
        <li>It is associative and distributive over addition</li>
      </ul>
      <p>
        The <strong>identity matrix</strong> {"$\\mathbf{I}$"} leaves any
        vector or matrix unchanged (analogous to multiplying by 1):
      </p>
      <F>
        {[
          "$$\\mathbf{I}_2 = \\begin{pmatrix}1&0\\\\0&1\\end{pmatrix}, \\quad \\mathbf{I}_3 = \\begin{pmatrix}1&0&0\\\\0&1&0\\\\0&0&1\\end{pmatrix}$$",
        ]}
      </F>

      {/* ── HOMOGENEOUS ── */}
      <h2 id="homogeneous">Homogeneous Coordinates</h2>
      <p>
        An extra dimension {"$w$"} is appended to points and vectors so that
        translation can be encoded in a matrix, enabling a unified
        transformation framework for all similarity and affine operations. They
        also support perspective projection in 3D graphics.
      </p>
      <ul>
        <li>
          <strong>Points</strong>: set {"$w = 1$"} — translation is applied
        </li>
        <li>
          <strong>Vectors</strong>: set {"$w = 0$"} — translation is suppressed
          (vectors have no position)
        </li>
      </ul>
      <F>
        {[
          "Point: $\\mathbf{p} = (x,\\, y,\\, 1)^T$ — translation affects it",
          "Vector: $\\mathbf{v} = (x,\\, y,\\, 0)^T$ — translation does not affect it",
        ]}
      </F>

      {/* ── HOMOGENEOUS 2D ── */}
      <h2 id="homogeneous-2d">Homogeneous Matrices (2D)</h2>

      <h3>Translation</h3>
      <F>
        {[
          "$$T = \\begin{pmatrix}1 & 0 & t_x \\\\ 0 & 1 & t_y \\\\ 0 & 0 & 1\\end{pmatrix}, \\quad T\\cdot\\mathbf{p} = \\begin{pmatrix}x+t_x \\\\ y+t_y \\\\ 1\\end{pmatrix}$$",
        ]}
      </F>

      <h3>Scale</h3>
      <F>
        {[
          "$$S = \\begin{pmatrix}s_x & 0 & 0 \\\\ 0 & s_y & 0 \\\\ 0 & 0 & 1\\end{pmatrix}$$",
        ]}
      </F>

      <h3>Rotation</h3>
      <F>
        {[
          "$$R(\\theta) = \\begin{pmatrix}\\cos\\theta & -\\sin\\theta & 0 \\\\ \\sin\\theta & \\cos\\theta & 0 \\\\ 0 & 0 & 1\\end{pmatrix}$$",
        ]}
      </F>

      <h3>Shear (horizontal)</h3>
      <F>
        {[
          "$$\\text{shear\\_x}(\\theta) = \\begin{pmatrix}1 & \\tan\\theta & 0 \\\\ 0 & 1 & 0 \\\\ 0 & 0 & 1\\end{pmatrix}$$",
        ]}
      </F>

      {/* ── HOMOGENEOUS 3D ── */}
      <h2 id="homogeneous-3d">3D Homogeneous Matrices</h2>

      <h3>3D Scale</h3>
      <F>
        {[
          "$$S = \\begin{pmatrix}s_x&0&0&0\\\\0&s_y&0&0\\\\0&0&s_z&0\\\\0&0&0&1\\end{pmatrix}$$",
        ]}
      </F>

      <h3>3D Translation</h3>
      <F>
        {[
          "$$T = \\begin{pmatrix}1&0&0&t_x\\\\0&1&0&t_y\\\\0&0&1&t_z\\\\0&0&0&1\\end{pmatrix}$$",
        ]}
      </F>

      {/* ── BASIS VECTORS ── */}
      <h2 id="basis-vectors">Basis Vectors</h2>
      <p>
        The <strong>columns</strong> of a transformation matrix represent the
        new positions of the coordinate basis vectors after the transformation.
        The identity matrix encodes the standard basis:
      </p>
      <F>
        {[
          "$x$-axis: $(1,0)$ — $y$-axis: $(0,1)$",
          "Rotation rotates the basis vectors; scaling changes their magnitude.",
        ]}
      </F>

      {/* ── 3D ROTATION ── */}
      <h2 id="rotation-3d">3D Rotation Matrices</h2>

      <h3>Right-Handed System</h3>
      <p>
        Positive rotation is <strong>counterclockwise</strong> when viewed
        along the positive axis direction (right-hand rule).
      </p>
      <F>
        {[
          "$$R_z(\\theta) = \\begin{pmatrix}\\cos\\theta&-\\sin\\theta&0&0\\\\\\sin\\theta&\\cos\\theta&0&0\\\\0&0&1&0\\\\0&0&0&1\\end{pmatrix}$$",
          "$$R_x(\\theta) = \\begin{pmatrix}1&0&0&0\\\\0&\\cos\\theta&-\\sin\\theta&0\\\\0&\\sin\\theta&\\cos\\theta&0\\\\0&0&0&1\\end{pmatrix}$$",
          "$$R_y(\\theta) = \\begin{pmatrix}\\cos\\theta&0&\\sin\\theta&0\\\\0&1&0&0\\\\-\\sin\\theta&0&\\cos\\theta&0\\\\0&0&0&1\\end{pmatrix}$$",
        ]}
      </F>
      <NoteBox>
        {"$R_y$"} has its sine terms flipped compared to {"$R_x$"} and{" "}
        {"$R_z$"} — because {"$y$"}-axis rotation affects the{"  "}
        {"$x$"}-{"$z$"} plane, where the{"  "}
        {"$z$"}-axis rotates toward the {"$x$"}-axis.
      </NoteBox>

      <h3>Left-Handed System</h3>
      <p>
        Positive rotation is <strong>clockwise</strong>. The sine terms in all
        three matrices swap signs compared to the right-handed versions.
      </p>
      <F>
        {[
          "$$R_z^\\text{LH}(\\theta) = \\begin{pmatrix}\\cos\\theta&\\sin\\theta&0&0\\\\-\\sin\\theta&\\cos\\theta&0&0\\\\0&0&1&0\\\\0&0&0&1\\end{pmatrix}$$",
        ]}
      </F>

      {/* ── EXTRINSIC / INTRINSIC ── */}
      <h2 id="extrinsic-intrinsic">Extrinsic vs Intrinsic Rotation</h2>

      <h3>Extrinsic Rotations</h3>
      <p>
        Each rotation is applied sequentially relative to the{" "}
        <em>fixed world (containing) frame</em>. Common in world-space
        transformations and external object manipulation.
      </p>
      <F>
        {"$M = R_z(\\gamma)\\cdot R_y(\\beta)\\cdot R_x(\\alpha)$ — rotations in global axes order"}
      </F>

      <h3>Intrinsic Rotations</h3>
      <p>
        Each rotation is applied relative to the{" "}
        <em>object's evolving local frame</em>. Preceding rotations reorient
        the local frame, affecting how subsequent angles are measured. Common
        in animation and simulations.
      </p>
      <NoteBox>
        Both methods can produce the same final rotation matrix, but they serve
        different conceptual use cases and require different angle inputs.
      </NoteBox>

      {/* ── EULER / GIMBAL ── */}
      <h2 id="euler-gimbal">Euler Angles &amp; Gimbal Lock</h2>
      <p>
        Euler angles describe 3D orientation as a sequence of three rotations
        around principal axes:
      </p>
      <ul>
        <li>
          <strong>Yaw</strong> — rotation around {"$z$"}-axis (left/right)
        </li>
        <li>
          <strong>Pitch</strong> — rotation around {"$y$"}-axis (up/down)
        </li>
        <li>
          <strong>Roll</strong> — rotation around {"$x$"}-axis (twist)
        </li>
      </ul>
      <p>
        Any rotation order (ZYX, XYZ, etc.) is valid. Angles can be applied
        intrinsically or extrinsically.
      </p>

      <h3>Gimbal Lock</h3>
      <p>
        Occurs when two rotation axes align (e.g. pitching 90° aligns the yaw
        and roll axes), reducing the effective degrees of freedom from 3 to 2.
        Causes issues in animations and controls where smooth, incremental
        rotation changes are needed.
      </p>
      <NoteBox label="Solution">
        Use <strong>quaternions</strong> or <strong>axis-angle</strong>{" "}
        representations to avoid gimbal lock.
      </NoteBox>

      {/* ── AXIS-ANGLE ── */}
      <h2 id="axis-angle">Axis-Angle Rotation</h2>
      <p>
        Describes any 3D rotation as an angle {"$\\theta$"} around a single
        unit vector axis {"$\\hat{\\mathbf{u}}$"}. Compact, intuitive, and
        avoids gimbal lock.
      </p>
      <F>{"$(\\hat{\\mathbf{u}},\\, \\theta)$ — axis can be a primary or arbitrary axis"}</F>

      <h3>Arbitrary Axis — Algorithm</h3>
      <ol>
        <li>
          Align the arbitrary axis with a principal axis (e.g. the {"$z$"}-axis)
          via translations and rotations
        </li>
        <li>Perform the rotation about that principal axis</li>
        <li>Undo the alignment (reverse the initial transformations)</li>
      </ol>

      {/* ── QUATERNIONS ── */}
      <h2 id="quaternions">Quaternions</h2>
      <p>
        A quaternion is a 4D representation of rotation consisting of a scalar
        part {"$w$"} and a 3D vector part {"$(x, y, z)$"}:
      </p>
      <F>{"$$q = w + x\\mathbf{i} + y\\mathbf{j} + z\\mathbf{k}$$"}</F>
      <p>Advantages over Euler angles:</p>
      <ul>
        <li>Avoid gimbal lock</li>
        <li>
          Efficient interpolation via <strong>SLERP</strong> (Spherical Linear
          intERPolation) for smooth animations
        </li>
        <li>Computationally stable when chaining multiple rotations</li>
      </ul>
      <h3>In Practice</h3>
      <ol>
        <li>Convert quaternion to rotation matrix for use with standard pipelines</li>
        <li>Combine with translation and scale for a complete {"$4\\times 4$"} matrix</li>
        <li>Use SLERP for smooth transitions between orientations</li>
      </ol>

      {/* ── COMPOUND ── */}
      <h2 id="compound">Compound Transformations</h2>
      <p>
        Multiple transformations are combined by multiplying their matrices.
        Because matrix multiplication is not commutative,{" "}
        <strong>order matters</strong>:
      </p>
      <F>
        {[
          "$T\\cdot R \\ne R\\cdot T$ in general",
          "Standard convention: $M = T\\cdot R\\cdot S$ (conceptually: scale first, then rotate, then translate)",
          "Often written as \"TRS\" for matrix multiplication order, \"SRT\" for conceptual operation order.",
        ]}
      </F>
      <NoteBox>
        Translating then rotating moves the object in an arc around the origin;
        rotating then translating moves it along the local axes. Always apply
        in the intended order.
      </NoteBox>

      {/* ── SIMILARITY VS AFFINE ── */}
      <h2 id="similarity-affine">Similarity vs Affine Transforms</h2>

      <h3>Decomposing Similarity Transforms</h3>
      <p>
        Any compound similarity transform (T, R, uniform S) can always be
        decomposed into a single {"$T\\cdot R\\cdot S$"}:
      </p>
      <F>
        {[
          "$M_{\\text{compound}} = T\\cdot R\\cdot S$ (with uniform, non-degenerate $S$)",
          "Works because uniform scaling introduces no shear and rotations are orthogonal.",
        ]}
      </F>
      <p>
        Simple decomposition from the matrix: extract {"$T$"} from the last
        column; normalise basis vectors of the upper-left {"$3\\times 3$"} for{" "}
        {"$R$"}; use their magnitude for {"$S$"}. For general cases use{" "}
        <strong>SVD</strong> (more expensive).
      </p>

      <h3>Affine Transforms and Shear</h3>
      <p>
        Chaining <em>non-uniform</em> scales with rotations can introduce
        shear — the resulting basis vectors are no longer orthogonal. You
        cannot represent hierarchical transformations with only T, R, S when
        scale can be non-uniform.
      </p>

      {/* ── SHEAR FROM CHAIN ── */}
      <h2 id="shear-chain">Shear from Chained Non-Uniform Scales &amp; Rotations</h2>
      <p>
        Given {"$M_1 = R_1\\cdot S_1$"} and {"$M_2 = R_2\\cdot S_2$"} where
        {"$S_1, S_2$"} are non-uniform, the combined matrix:
      </p>
      <F>{"$M = M_2\\cdot M_1$"}</F>
      <p>
        produces mixed scaling-rotation terms that skew the basis vectors. To
        test for shear, compute the dot product of the resulting basis vectors
        {"$\\mathbf{u}\\cdot\\mathbf{v}$"}: if non-zero, the axes are not
        orthogonal and shear is present.
      </p>
      <F>
        {[
          "$\\cos\\theta_{\\mathbf{u},\\mathbf{v}} = \\dfrac{\\mathbf{u}\\cdot\\mathbf{v}}{\\|\\mathbf{u}\\|\\,\\|\\mathbf{v}\\|}$",
          "If $\\theta \\ne 90°$, shear is present.",
        ]}
      </F>

      {/* ── INVERSE ── */}
      <h2 id="inverse">Inverse Transformations</h2>
      <p>
        The inverse {"$T^{-1}$"} of transformation {"$T$"} satisfies{" "}
        {"$T\\cdot T^{-1} = \\mathbf{I}$"}. Used to undo transformations and
        convert between coordinate spaces.
      </p>

      <h3>Simple Inverses</h3>
      <F>
        {[
          "Scale: $S^{-1}(s_x,s_y,s_z) = S(1/s_x,\\,1/s_y,\\,1/s_z)$",
          "Rotation: $R^{-1} = R^T$ (transpose = inverse for orthogonal matrices)",
          "Translation: $T^{-1}(t_x,t_y,t_z) = T(-t_x,\\,-t_y,\\,-t_z)$",
        ]}
      </F>

      <h3>Composite Inverse</h3>
      <p>Reverse the order and invert each component:</p>
      <F>
        {[
          "$$(T\\cdot R\\cdot S)^{-1} = S^{-1}\\cdot R^{-1}\\cdot T^{-1}$$",
        ]}
      </F>
      <p>
        You must know the constituent transformations and their order to apply
        this method. For an unknown matrix that is a similarity transform, use
        the decomposition method; otherwise use SVD (expensive, generally
        avoided in high-performance graphics).
      </p>

      {/* ── NON-INVERTIBLE ── */}
      <h2 id="non-invertible">Non-Invertible Transforms</h2>
      <p>
        A transformation that collapses one or more dimensions has no inverse.
        Common examples:
      </p>
      <ul>
        <li>Scaling by zero along any axis</li>
        <li>Orthographic projection (3D → 2D)</li>
        <li>Any degenerate scaling</li>
      </ul>
      <h3>Determinant Test</h3>
      <F>
        {[
          "$$\\det(T) = 0 \\implies \\text{transformation is non-invertible}$$",
          "The determinant measures the volume scaling factor; zero means at least one dimension was collapsed.",
        ]}
      </F>

      <h3>{"$4\\times 4$"} Matrix Inversion (Adjugate Method)</h3>
      <ol>
        <li>Compute the adjoint (adjugate) matrix</li>
        <li>Divide by the determinant (if non-zero)</li>
      </ol>
      <NoteBox>
        Avoid the general determinant method for large matrices. Prefer LU
        decomposition or iterative methods for larger systems.
      </NoteBox>

      {/* ── NUMERICAL ── */}
      <h2 id="numerical">Numerical Accuracy</h2>
      <p>
        Floating-point representation introduces precision limits that affect
        transformations:
      </p>
      <ul>
        <li>
          Extreme scale values (very large or very small) cause rounding
          — scaling by {"$10^6$"} then {"$10^{-6}$"} may not return the original
          value exactly
        </li>
        <li>
          When {"$\\det(T)$"} is very small (near-singular), inversion errors
          amplify — indicated by a high <strong>condition number</strong>
        </li>
        <li>
          Repeated inversions compound floating-point errors — avoid{" "}
          {"$\\text{Inverse}(\\text{Inverse}(M))$"} patterns
        </li>
      </ul>
      <F>
        {[
          "Condition number: measures sensitivity of a matrix to numerical errors during inversion.",
          "High condition number ≈ near-singular ≈ numerically unstable inversion.",
        ]}
      </F>

      <h3>Summary of Inverse Transforms</h3>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "1rem", fontSize: "0.88rem" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
            <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontFamily: "system-ui", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>Transform</th>
            <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontFamily: "system-ui", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>Inverse</th>
            <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontFamily: "system-ui", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {[
            ["Scale $S(s_x,s_y,s_z)$", "$S(1/s_x,1/s_y,1/s_z)$", "Requires $s \\ne 0$"],
            ["Rotation $R$", "$R^T$ (transpose)", "Only valid for orthogonal matrices"],
            ["Translation $T(t_x,t_y,t_z)$", "$T(-t_x,-t_y,-t_z)$", "Always invertible"],
            ["Composite $T\\!\\cdot\\!R\\!\\cdot\\!S$", "$S^{-1}\\!\\cdot\\!R^{-1}\\!\\cdot\\!T^{-1}$", "Reverse order"],
            ["Scale by 0 / projection", "Does not exist", "$\\det = 0$, dimension lost"],
          ].map(([t, inv, note], i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
              <td style={{ padding: "8px 12px", color: "#374151" }}>{t}</td>
              <td style={{ padding: "8px 12px", color: "#374151" }}>{inv}</td>
              <td style={{ padding: "8px 12px", color: "#6b7280", fontSize: "0.82rem" }}>{note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Transformations;
